import { useState, useEffect, useCallback } from "react";
import { financeApi, bereavementsApi, settingsApi } from "@/lib/api";
import { toast } from "sonner";
import { Bank, CurrencyDollar, Receipt, ChartLine, Plus, Trash, X, ArrowUp, ArrowDown, HandCoins, NotePencil } from "@phosphor-icons/react";
import { format } from "date-fns";
import { bereavementSubtitle } from "@/lib/bereavement";

const fmt = (n) => `KES ${Math.round(Number(n || 0)).toLocaleString()}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function BankingTab() {
  const [section, setSection] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bereavements, setBereavements] = useState([]);
  const [settings, setSettings] = useState({});
  const [report, setReport] = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  // Modals
  const [showAddTxn, setShowAddTxn] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddPayout, setShowAddPayout] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showBankSettings, setShowBankSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, txns, exps, pos, cats, bs, st, rp] = await Promise.all([
        financeApi.overview(),
        financeApi.listTransactions(),
        financeApi.listExpenses(),
        financeApi.listPayouts(),
        financeApi.listCategories(),
        bereavementsApi.list(),
        settingsApi.get(),
        financeApi.report(period),
      ]);
      setOverview(ov.data);
      setTransactions(txns.data);
      setExpenses(exps.data);
      setPayouts(pos.data);
      setCategories(cats.data);
      setBereavements(bs.data);
      setSettings(st.data);
      setReport(rp.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load finance data");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const sections = [
    { key: "overview",     label: "Overview",     icon: ChartLine },
    { key: "transactions", label: "Bank ledger",  icon: Bank },
    { key: "expenses",     label: "Expenses",     icon: Receipt },
    { key: "payouts",      label: "Payouts",      icon: HandCoins },
    { key: "report",       label: "Reports",      icon: CurrencyDollar },
  ];

  if (loading || !overview) return <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">Loading finance data...</div>;

  return (
    <div className="space-y-4" data-testid="banking-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Bank className="w-5 h-5" /> Banking & Expenses</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage the welfare bank account, expenses and benefit payouts.</p>
        </div>
        <button onClick={() => setShowBankSettings(true)} data-testid="banking-settings-btn" className="text-xs px-3 py-1.5 rounded-lg bg-secondary text-secondary-foreground font-medium flex items-center gap-1"><NotePencil className="w-3.5 h-3.5" />Bank settings</button>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-xl overflow-x-auto">
        {sections.map(s => (
          <button key={s.key} onClick={() => setSection(s.key)} data-testid={`banking-section-${s.key}`}
            className={`flex-1 min-w-max flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${section === s.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
            <s.icon className="w-3.5 h-3.5" />
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      {section === "overview" && <Overview overview={overview} bereavements={bereavements} categories={categories} />}
      {section === "transactions" && <Transactions transactions={transactions} bereavements={bereavements} onAdd={() => setShowAddTxn(true)} onDelete={async (id) => { await financeApi.deleteTransaction(id); toast.success("Transaction deleted"); load(); }} />}
      {section === "expenses" && <Expenses expenses={expenses} bereavements={bereavements} categories={categories} onAdd={() => setShowAddExpense(true)} onManageCats={() => setShowCategories(true)} onDelete={async (id) => { await financeApi.deleteExpense(id); toast.success("Expense deleted"); load(); }} />}
      {section === "payouts" && <Payouts payouts={payouts} bereavements={bereavements} standardAmount={settings?.standardBenefitAmount || 0} onAdd={() => setShowAddPayout(true)} onDelete={async (id) => { await financeApi.deletePayout(id); toast.success("Payout deleted"); load(); }} />}
      {section === "report" && <ReportView report={report} period={period} onPeriodChange={setPeriod} />}

      {/* Modals */}
      {showAddTxn && <AddTransactionModal bereavements={bereavements} onClose={() => setShowAddTxn(false)} onSaved={() => { setShowAddTxn(false); load(); }} />}
      {showAddExpense && <AddExpenseModal categories={categories} bereavements={bereavements} onClose={() => setShowAddExpense(false)} onSaved={() => { setShowAddExpense(false); load(); }} />}
      {showAddPayout && <AddPayoutModal bereavements={bereavements} standardAmount={settings?.standardBenefitAmount || 0} onClose={() => setShowAddPayout(false)} onSaved={() => { setShowAddPayout(false); load(); }} />}
      {showCategories && <CategoriesModal categories={categories} onClose={() => setShowCategories(false)} onChanged={load} />}
      {showBankSettings && <BankSettingsModal settings={settings} onClose={() => setShowBankSettings(false)} onSaved={() => { setShowBankSettings(false); load(); }} />}
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────
function Overview({ overview, bereavements }) {
  const acc = overview.bankAccount;
  const t = overview.totals;
  const c = overview.counts;
  return (
    <div className="space-y-4">
      {/* Bank balance hero */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-2xl p-6 shadow-lg" data-testid="bank-balance-card">
        <div className="flex items-center gap-2 text-sm opacity-90"><Bank className="w-4 h-4" /> {acc.name || "Bank account"} {acc.accountNumber && <span className="font-mono">· ****{acc.accountNumber.slice(-4)}</span>}</div>
        <p className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="bank-balance">{fmt(acc.balance)}</p>
        <div className="flex gap-4 mt-3 text-xs opacity-90">
          <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3" /> Deposits: {fmt(acc.deposits)}</span>
          <span className="flex items-center gap-1"><ArrowDown className="w-3 h-3" /> Withdrawals: {fmt(acc.withdrawals)}</span>
        </div>
      </div>

      {/* Totals grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Contributions collected" value={fmt(t.contributionsCollected)} accent="text-green-700" />
        <Stat label="Total expenses" value={fmt(t.expenses)} accent="text-red-600" />
        <Stat label="Benefit payouts" value={fmt(t.benefitPayouts)} accent="text-orange-600" />
        <Stat label="Net surplus" value={fmt(t.netSurplus)} accent={t.netSurplus >= 0 ? "text-emerald-700" : "text-red-700"} sub={`Std payout: ${fmt(t.standardBenefitAmount)}`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Bank transactions" value={c.transactions} />
        <Stat label="Expenses logged" value={c.expenses} />
        <Stat label="Payouts recorded" value={c.payouts} />
        <Stat label="Bereavements" value={c.bereavements} />
      </div>

      {/* Per-bereavement P&L table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
          <p className="font-heading font-semibold text-sm">Per-bereavement P&L</p>
          <span className="text-xs text-muted-foreground">{bereavements.length} bereavements</span>
        </div>
        <BereavementPnlList bereavements={bereavements} />
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-foreground", sub }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-heading text-lg font-bold mt-1 ${accent}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function BereavementPnlList({ bereavements }) {
  const [pnls, setPnls] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = {};
      await Promise.all(bereavements.slice(0, 50).map(async (b) => {
        try { const { data } = await financeApi.pnl(b.id); result[b.id] = data; } catch {}
      }));
      if (!cancelled) setPnls(result);
    })();
    return () => { cancelled = true; };
  }, [bereavements]);
  if (!bereavements.length) return <p className="p-6 text-center text-sm text-muted-foreground">No bereavements yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted">
          <tr>
            <th className="text-left px-3 py-2">Bereavement</th>
            <th className="text-right px-3 py-2">Collected</th>
            <th className="text-right px-3 py-2">Expenses</th>
            <th className="text-right px-3 py-2">Payouts</th>
            <th className="text-right px-3 py-2">Surplus</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {bereavements.map(b => {
            const p = pnls[b.id];
            return (
              <tr key={b.id} className="hover:bg-muted/30">
                <td className="px-3 py-2"><div className="font-medium truncate max-w-xs">{b.deceasedName}</div><div className="text-[10px] text-muted-foreground">{bereavementSubtitle(b)}</div></td>
                <td className="text-right px-3 py-2 font-mono">{p ? fmt(p.totalCollected) : "..."}</td>
                <td className="text-right px-3 py-2 font-mono text-red-600">{p ? fmt(p.totalExpenses) : "..."}</td>
                <td className="text-right px-3 py-2 font-mono text-orange-600">{p ? fmt(p.totalPayouts) : "..."}</td>
                <td className={`text-right px-3 py-2 font-mono font-semibold ${p && p.surplus < 0 ? "text-red-600" : "text-emerald-700"}`}>{p ? fmt(p.surplus) : "..."}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Transactions ────────────────────────────────────────────────────────────
function Transactions({ transactions, bereavements, onAdd, onDelete }) {
  const beMap = Object.fromEntries(bereavements.map(b => [b.id, b.deceasedName]));
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="bank-transactions">
      <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
        <p className="font-heading font-semibold text-sm">Bank ledger ({transactions.length})</p>
        <button onClick={onAdd} data-testid="add-transaction-btn" className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Add transaction</button>
      </div>
      {transactions.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">No transactions yet.</p> :
        <ul className="divide-y divide-border">
          {transactions.map(t => (
            <li key={t.id} className="px-4 py-3 flex items-center gap-3" data-testid={`transaction-row-${t.id}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.type === "deposit" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                {t.type === "deposit" ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{t.description || (t.type === "deposit" ? "Deposit" : "Withdrawal")}</p>
                <p className="text-xs text-muted-foreground">{t.date} {t.reference && `· Ref: ${t.reference}`} {t.bereavementId && beMap[t.bereavementId] && `· ${beMap[t.bereavementId]}`}</p>
              </div>
              <p className={`font-mono font-semibold text-sm ${t.type === "deposit" ? "text-emerald-700" : "text-red-700"}`}>{t.type === "deposit" ? "+" : "−"}{fmt(t.amount)}</p>
              <button onClick={() => onDelete(t.id)} className="p-1.5 text-muted-foreground hover:text-red-600" data-testid={`delete-transaction-${t.id}`}><Trash className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// ─── Expenses ────────────────────────────────────────────────────────────────
function Expenses({ expenses, bereavements, categories, onAdd, onManageCats, onDelete }) {
  const beMap = Object.fromEntries(bereavements.map(b => [b.id, b.deceasedName]));
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="expenses">
      <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center justify-between gap-2 flex-wrap">
        <p className="font-heading font-semibold text-sm">Expenses ({expenses.length})</p>
        <div className="flex gap-2">
          <button onClick={onManageCats} className="text-xs px-3 py-1.5 rounded-lg bg-muted text-muted-foreground font-medium flex items-center gap-1" data-testid="manage-categories-btn"><NotePencil className="w-3.5 h-3.5" />Categories ({categories.length})</button>
          <button onClick={onAdd} data-testid="add-expense-btn" className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Add expense</button>
        </div>
      </div>
      {expenses.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">No expenses logged.</p> :
        <ul className="divide-y divide-border">
          {expenses.map(e => (
            <li key={e.id} className="px-4 py-3 flex items-center gap-3" data-testid={`expense-row-${e.id}`}>
              <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center"><Receipt className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{e.description || e.categoryName}</p>
                <p className="text-xs text-muted-foreground">{e.date} · {e.categoryName} {e.bereavementId && beMap[e.bereavementId] && `· ${beMap[e.bereavementId]}`}</p>
              </div>
              <p className="font-mono font-semibold text-sm text-red-600">{fmt(e.amount)}</p>
              <button onClick={() => onDelete(e.id)} className="p-1.5 text-muted-foreground hover:text-red-600"><Trash className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// ─── Payouts ─────────────────────────────────────────────────────────────────
function Payouts({ payouts, bereavements, standardAmount, onAdd, onDelete }) {
  const beMap = Object.fromEntries(bereavements.map(b => [b.id, b.deceasedName]));
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="payouts">
      <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-heading font-semibold text-sm">Benefit payouts ({payouts.length})</p>
          <p className="text-xs text-muted-foreground">Standard amount: <span className="font-semibold">{fmt(standardAmount)}</span></p>
        </div>
        <button onClick={onAdd} data-testid="add-payout-btn" className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Record payout</button>
      </div>
      {payouts.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">No payouts recorded.</p> :
        <ul className="divide-y divide-border">
          {payouts.map(p => (
            <li key={p.id} className="px-4 py-3 flex items-center gap-3" data-testid={`payout-row-${p.id}`}>
              <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center"><HandCoins className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{p.paidTo || "Family"} {beMap[p.bereavementId] && `— ${beMap[p.bereavementId]}`}</p>
                <p className="text-xs text-muted-foreground">{p.date} {p.paidVia && `· ${p.paidVia}`} {p.description && `· ${p.description}`}</p>
              </div>
              <p className="font-mono font-semibold text-sm text-orange-700">{fmt(p.amount)}</p>
              <button onClick={() => onDelete(p.id)} className="p-1.5 text-muted-foreground hover:text-red-600"><Trash className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// ─── Report ──────────────────────────────────────────────────────────────────
function ReportView({ report, period, onPeriodChange }) {
  if (!report) return null;
  return (
    <div className="space-y-4" data-testid="report-view">
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <p className="font-heading font-semibold text-sm">Management report</p>
          <select value={period} onChange={(e) => onPeriodChange(e.target.value)} className="px-3 py-1.5 text-xs bg-card border border-input rounded-lg" data-testid="report-period-select">
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="quarter">Last quarter</option>
            <option value="year">Last year</option>
            <option value="all">All time</option>
          </select>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Income" value={fmt(report.income)} accent="text-emerald-700" sub={`${report.contributionCount} contributions`} />
          <Stat label="Expenses" value={fmt(report.expenses)} accent="text-red-600" sub={`${report.expenseCount} entries`} />
          <Stat label="Payouts" value={fmt(report.payouts)} accent="text-orange-600" sub={`${report.payoutCount} payouts`} />
          <Stat label="Net" value={fmt(report.net)} accent={report.net >= 0 ? "text-emerald-700" : "text-red-700"} />
        </div>
      </div>

      {report.expenseByCategory.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="font-heading font-semibold text-sm mb-3">Expenses by category</p>
          <div className="space-y-2">
            {report.expenseByCategory.map((row, i) => {
              const max = report.expenseByCategory[0]?.amount || 1;
              const pct = (row.amount / max) * 100;
              return (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1"><span>{row.category}</span><span className="font-mono font-semibold">{fmt(row.amount)}</span></div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-orange-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────────────────────
function ModalShell({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid={testId}>
        <div className="flex items-center justify-between"><h3 className="font-heading text-xl font-semibold">{title}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

function AddTransactionModal({ bereavements, onClose, onSaved }) {
  const [form, setForm] = useState({ type: "deposit", amount: "", date: today(), description: "", reference: "", bereavementId: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.amount || !form.date) { toast.error("Amount + date required"); return; }
    setSaving(true);
    try { await financeApi.createTransaction({ ...form, amount: Number(form.amount), bereavementId: form.bereavementId || null }); toast.success("Transaction recorded"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title="Bank transaction" testId="add-transaction-modal" onClose={onClose}>
      <div><label className="text-sm font-medium block mb-1">Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="txn-type"><option value="deposit">Deposit</option><option value="withdrawal">Withdrawal</option></select></div>
      <div><label className="text-sm font-medium block mb-1">Amount (KES) *</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="txn-amount" /></div>
      <div><label className="text-sm font-medium block mb-1">Date *</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="txn-date" /></div>
      <div><label className="text-sm font-medium block mb-1">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Bank deposit, M-Pesa withdrawal" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="txn-desc" /></div>
      <div><label className="text-sm font-medium block mb-1">Reference</label><input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Cheque no, M-Pesa code..." className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="txn-ref" /></div>
      <div><label className="text-sm font-medium block mb-1">Tied to bereavement (optional)</label><select value={form.bereavementId} onChange={(e) => setForm({ ...form, bereavementId: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="txn-bereavement"><option value="">None</option>{bereavements.map(b => <option key={b.id} value={b.id}>{b.deceasedName} ({b.relationship})</option>)}</select></div>
      <button onClick={submit} disabled={saving} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60" data-testid="txn-save-btn">{saving ? "Saving..." : "Save transaction"}</button>
    </ModalShell>
  );
}

function AddExpenseModal({ categories, bereavements, onClose, onSaved }) {
  const [form, setForm] = useState({ categoryId: categories[0]?.id || "", amount: "", date: today(), description: "", bereavementId: "", paidVia: "bank" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.categoryId || !form.amount || !form.date) { toast.error("Category, amount & date required"); return; }
    setSaving(true);
    try { await financeApi.createExpense({ ...form, amount: Number(form.amount), bereavementId: form.bereavementId || null }); toast.success("Expense recorded"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title="Record expense" testId="add-expense-modal" onClose={onClose}>
      <div><label className="text-sm font-medium block mb-1">Category *</label><select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="exp-category">{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
      <div><label className="text-sm font-medium block mb-1">Amount (KES) *</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="exp-amount" /></div>
      <div><label className="text-sm font-medium block mb-1">Date *</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="exp-date" /></div>
      <div><label className="text-sm font-medium block mb-1">Paid via</label><select value={form.paidVia} onChange={(e) => setForm({ ...form, paidVia: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="exp-paidvia"><option value="bank">Bank</option><option value="cash">Cash</option><option value="mpesa">M-Pesa</option></select></div>
      <div><label className="text-sm font-medium block mb-1">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="exp-desc" /></div>
      <div><label className="text-sm font-medium block mb-1">Tied to bereavement (optional)</label><select value={form.bereavementId} onChange={(e) => setForm({ ...form, bereavementId: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="exp-bereavement"><option value="">None — general expense</option>{bereavements.map(b => <option key={b.id} value={b.id}>{b.deceasedName} ({b.relationship})</option>)}</select></div>
      <button onClick={submit} disabled={saving} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60" data-testid="exp-save-btn">{saving ? "Saving..." : "Save expense"}</button>
    </ModalShell>
  );
}

function AddPayoutModal({ bereavements, standardAmount, onClose, onSaved }) {
  const [form, setForm] = useState({ bereavementId: "", amount: standardAmount || "", date: today(), paidTo: "", description: "", paidVia: "bank" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.bereavementId || !form.amount || !form.date) { toast.error("Bereavement, amount & date required"); return; }
    setSaving(true);
    try { await financeApi.createPayout({ ...form, amount: Number(form.amount) }); toast.success("Payout recorded"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title="Record benefit payout" testId="add-payout-modal" onClose={onClose}>
      <div><label className="text-sm font-medium block mb-1">Bereavement *</label><select value={form.bereavementId} onChange={(e) => setForm({ ...form, bereavementId: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="payout-bereavement"><option value="">Select bereavement</option>{bereavements.map(b => <option key={b.id} value={b.id}>{b.deceasedName} ({b.relationship} of {b.memberName})</option>)}</select></div>
      <div><label className="text-sm font-medium block mb-1">Amount (KES) *</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="payout-amount" /><p className="text-xs text-muted-foreground mt-1">Standard payout: {fmt(standardAmount)}</p></div>
      <div><label className="text-sm font-medium block mb-1">Date *</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="payout-date" /></div>
      <div><label className="text-sm font-medium block mb-1">Paid to</label><input value={form.paidTo} onChange={(e) => setForm({ ...form, paidTo: e.target.value })} placeholder="Recipient name" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="payout-to" /></div>
      <div><label className="text-sm font-medium block mb-1">Paid via</label><select value={form.paidVia} onChange={(e) => setForm({ ...form, paidVia: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="payout-paidvia"><option value="bank">Bank</option><option value="cash">Cash</option><option value="mpesa">M-Pesa</option></select></div>
      <div><label className="text-sm font-medium block mb-1">Notes</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="payout-desc" /></div>
      <button onClick={submit} disabled={saving} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60" data-testid="payout-save-btn">{saving ? "Saving..." : "Save payout"}</button>
    </ModalShell>
  );
}

function CategoriesModal({ categories, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await financeApi.createCategory({ name, description: desc }); setName(""); setDesc(""); toast.success("Category added"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    try { await financeApi.deleteCategory(id); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <ModalShell title="Expense categories" testId="categories-modal" onClose={onClose}>
      <ul className="divide-y divide-border rounded-xl border border-border">
        {categories.map(c => (
          <li key={c.id} className="flex items-center justify-between px-3 py-2 gap-2" data-testid={`cat-row-${c.id}`}>
            <div className="min-w-0"><p className="text-sm font-medium truncate">{c.name}</p>{c.description && <p className="text-xs text-muted-foreground truncate">{c.description}</p>}</div>
            <button onClick={() => del(c.id)} className="p-1.5 text-muted-foreground hover:text-red-600" data-testid={`cat-delete-${c.id}`}><Trash className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-sm font-medium">Add new category</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Category name" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="new-cat-name" />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="new-cat-desc" />
        <button onClick={add} className="w-full py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-sm" data-testid="new-cat-add">Add category</button>
      </div>
    </ModalShell>
  );
}

function BankSettingsModal({ settings, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: settings?.bankAccount?.name || "",
    accountNumber: settings?.bankAccount?.accountNumber || "",
    openingBalance: settings?.bankAccount?.openingBalance || 0,
    standardBenefitAmount: settings?.standardBenefitAmount || 100000,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await settingsApi.update({
        bankAccount: { name: form.name, accountNumber: form.accountNumber, openingBalance: Number(form.openingBalance) || 0 },
        standardBenefitAmount: Number(form.standardBenefitAmount) || 0,
      });
      toast.success("Bank settings saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title="Bank account settings" testId="bank-settings-modal" onClose={onClose}>
      <div><label className="text-sm font-medium block mb-1">Bank account name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Equity Bank — Utheri Welfare" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="bank-name" /></div>
      <div><label className="text-sm font-medium block mb-1">Account number</label><input value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} placeholder="Last 4 will be shown publicly" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="bank-acc-no" /></div>
      <div><label className="text-sm font-medium block mb-1">Opening balance (KES)</label><input type="number" value={form.openingBalance} onChange={(e) => setForm({ ...form, openingBalance: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="bank-opening" /><p className="text-xs text-muted-foreground mt-1">Balance carried forward before any logged transactions.</p></div>
      <div><label className="text-sm font-medium block mb-1">Standard benefit payout (KES)</label><input type="number" value={form.standardBenefitAmount} onChange={(e) => setForm({ ...form, standardBenefitAmount: e.target.value })} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="standard-benefit" /></div>
      <button onClick={submit} disabled={saving} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60" data-testid="bank-save-btn">{saving ? "Saving..." : "Save settings"}</button>
    </ModalShell>
  );
}
